import { useCallback, useEffect, useMemo, useState } from "react";
import { BACKEND_ENDPOINTS, getBackendBaseUrl } from "../config/backend";
import { DEFAULT_CATALOG } from "../config/catalog";

const mapHealthPayload = (payload) => {
  if (!payload) {
    return { ok: false, status: "unknown" };
  }

  const status = payload.status ?? "unknown";
  const normalizedStatus = status.toLowerCase();

  return {
    ok: normalizedStatus === "healthy",
    status,
    timestamp: payload.timestamp ?? null,
    version: payload.version ?? null,
    raw: payload,
  };
};

const buildHealthError = (message) => ({
  ok: false,
  status: "offline",
  message,
});

export const useCatalogData = () => {
  const baseUrl = useMemo(() => getBackendBaseUrl(), []);
  const [catalog, setCatalog] = useState(DEFAULT_CATALOG);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(
    async (signal) => {
      setLoading(true);
      setError(null);

      try {
        const healthPromise = fetch(`${baseUrl}${BACKEND_ENDPOINTS.health}`, {
          signal,
          headers: { Accept: "application/json" },
        });

        const catalogPromise = fetch(`${baseUrl}${BACKEND_ENDPOINTS.catalog}`, {
          signal,
          headers: { Accept: "application/json" },
        });

        const [healthResult, catalogResult] = await Promise.allSettled([
          healthPromise,
          catalogPromise,
        ]);

        if (signal?.aborted) {
          return;
        }

        // Health
        if (healthResult.status === "fulfilled") {
          const response = healthResult.value;
          if (response.ok) {
            const payload = await response.json().catch(() => null);
            if (!signal?.aborted) {
              setHealth(mapHealthPayload(payload));
            }
          } else {
            if (!signal?.aborted) {
              setHealth(buildHealthError(`HTTP ${response.status}`));
            }
          }
        } else {
          if (!signal?.aborted) {
            setHealth(buildHealthError(healthResult.reason?.message ?? "Sin respuesta"));
          }
        }

        // Catalog
        if (catalogResult.status === "fulfilled") {
          const response = catalogResult.value;
          if (response.ok) {
            const payload = await response.json().catch(() => null);
            if (payload && typeof payload === "object" && !signal?.aborted) {
              setCatalog(payload);
            }
          } else if (!signal?.aborted) {
            setError(
              (prev) =>
                prev ??
                new Error(
                  `No se pudo cargar el catálogo (HTTP ${response.status}).`
                )
            );
          }
        } else if (!signal?.aborted) {
          setError(
            (prev) =>
              prev ??
              new Error(
                catalogResult.reason?.message ?? "No se pudo cargar el catálogo."
              )
          );
        }
      } catch (err) {
        if (!signal?.aborted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [baseUrl]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const refresh = useCallback(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
  }, [fetchData]);

  return {
    baseUrl,
    catalog,
    health,
    loading,
    error,
    refresh,
  };
};
